import { action, makeObservable, observable, runInAction } from "mobx";
import InventoryAPI from "../api/InventoryAPI";
import PartnerCustomerInventoryTagsAPI from "../api/PartnerCustomerInventoryTagsAPI";
import {
  InventoryAdjustImportCsvResult,
  InventoryAdjustRequest,
  InventoryInboundImportCsvResult,
  InventoryInboundRequest,
  InventorySkuImportCsvResult,
  InventoryMovementLine,
  InventoryMovementRow,
  InventoryPack,
  InventoryPackCreateRequest,
  InventoryPackItem,
  InventoryPackItemInput,
  InventoryPackSummary,
  InventoryPackUpdateRequest,
  InventoryVariant,
  InventoryVariantUpdateRequest,
} from "../../../models/inventory/InventoryAdmin";
import { PartnerCustomerInventoryTag } from "../../../models/inventory/PartnerCustomerInventoryTag";
import { InventoryListing } from "../../../models/inventory/InventoryListing";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import { SortType } from "../../../models/SortType";
import CatalogProductsAPI from "../api/CatalogProductsAPI";
import {
  ProductListApi,
  ProductVariantApi,
} from "../../../models/CatalogProducts/types";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

export type InventoryDetailsListParams = {
  search?: string;
  tagId?: string;
  page_no?: number;
  limit?: number;
  sortBy?: string;
  sortType?: SortType;
};

export type InventoryInwardLineInput = {
  clientSku: string;
  productId: number;
  sku?: string;
  catalogSku?: string;
  qty: number;
  packSize?: number;
  boxSize?: string;
  tagId?: string;
  minThreshold?: number;
};

export class InventoryDetailsStore {
  private inventoryAPI: InventoryAPI;
  private partnerCustomerInventoryTagsAPI: PartnerCustomerInventoryTagsAPI;
  private catalogProductsAPI: CatalogProductsAPI;

  inventoryId: number | null = null;
  customerId: number | null = null;
  variants: InventoryVariant[] = [];
  packs: InventoryPackSummary[] = [];
  customerTags: PartnerCustomerInventoryTag[] = [];

  variantsPageNo = DEFAULT_PAGE;
  variantsLimit = DEFAULT_LIMIT;
  variantsTotalCount = 0;
  variantsTotalPages = 0;
  variantsSortBy = "";
  variantsSortType: SortType = SortType.DESC;

  packsPageNo = DEFAULT_PAGE;
  packsLimit = DEFAULT_LIMIT;
  packsTotalCount = 0;
  packsTotalPages = 0;
  packsSortBy = "";
  packsSortType: SortType = SortType.DESC;

  private lastVariantsParams: Pick<
    InventoryDetailsListParams,
    "search" | "tagId" | "sortBy" | "sortType"
  > = {};
  private lastPacksParams: Pick<
    InventoryDetailsListParams,
    "search" | "sortBy" | "sortType"
  > = {};

  constructor() {
    this.inventoryAPI = new InventoryAPI();
    this.partnerCustomerInventoryTagsAPI =
      new PartnerCustomerInventoryTagsAPI();
    this.catalogProductsAPI = new CatalogProductsAPI();

    makeObservable(this, {
      inventoryId: observable,
      customerId: observable,
      variants: observable,
      packs: observable,
      customerTags: observable,
      variantsPageNo: observable,
      variantsLimit: observable,
      variantsTotalCount: observable,
      variantsTotalPages: observable,
      variantsSortBy: observable,
      variantsSortType: observable,
      packsPageNo: observable,
      packsLimit: observable,
      packsTotalCount: observable,
      packsTotalPages: observable,
      packsSortBy: observable,
      packsSortType: observable,
      load: action,
      loadVariants: action,
      loadPacks: action,
      loadCustomerTags: action,
      createCustomerTag: action,
      resolveCustomerId: action,
      getPackItems: action,
      createPack: action,
      patchPack: action,
      deletePack: action,
      searchVariants: action,
      replacePackItems: action,
      getSkuMovements: action,
      adjustItem: action,
      adjustImportCsv: action,
      inboundImportCsv: action,
      importSkuCsv: action,
      updateVariantMeta: action,
      deleteVariantBySku: action,
      inwardVariants: action,
      searchProductsBySku: action,
      fetchProductVariants: action,
    });
  }

  private normalizeLimit = (limit?: number) => {
    if (limit == null || !Number.isFinite(limit) || limit <= 0) {
      return DEFAULT_LIMIT;
    }
    return Math.min(Math.floor(limit), MAX_LIMIT);
  };

  private normalizePage = (page?: number) => {
    if (page == null || !Number.isFinite(page) || page <= 0) {
      return DEFAULT_PAGE;
    }
    return Math.floor(page);
  };

  resolveCustomerId = async (
    inventoryId: number,
    preferredCustomerId?: number,
  ): Promise<number | null> => {
    try {
      if (
        preferredCustomerId != null &&
        Number.isFinite(preferredCustomerId) &&
        preferredCustomerId > 0
      ) {
        runInAction(() => {
          this.customerId = preferredCustomerId;
        });
        return preferredCustomerId;
      }

      const response = (await this.inventoryAPI.getListing({
        page_no: 1,
        limit: 1,
        sort_by: "created_date",
        sort_type: SortType.DESC,
        inventory_id: [inventoryId],
        order_id: [],
        customer_id: [],
      })) as PaginatedAPIResponse<InventoryListing>;

      const customerId = response?.list?.[0]?.customer_id ?? null;
      runInAction(() => {
        this.customerId =
          customerId != null && Number.isFinite(Number(customerId))
            ? Number(customerId)
            : null;
      });
      return this.customerId;
    } catch (error) {
      runInAction(() => {
        this.customerId = null;
      });
      throw error;
    }
  };

  loadCustomerTags = async (customerId: number): Promise<void> => {
    try {
      if (!customerId) {
        runInAction(() => {
          this.customerTags = [];
        });
        return;
      }

      const tags =
        await this.partnerCustomerInventoryTagsAPI.getTagsByCustomerId(
          customerId,
        );
      runInAction(() => {
        this.customerTags = tags ?? [];
      });
    } catch (error) {
      runInAction(() => {
        this.customerTags = [];
      });
      throw error;
    }
  };

  /** Creates a partner tag, reloads `customerTags` and returns the new tag. */
  createCustomerTag = async (
    customerId: number,
    tagId: string,
    tag: string,
  ): Promise<PartnerCustomerInventoryTag> => {
    try {
      const trimmedTagId = tagId.trim();
      await this.partnerCustomerInventoryTagsAPI.createTag({
        customer_id: customerId,
        tag_id: trimmedTagId,
        tag: tag.trim(),
      });
      await this.loadCustomerTags(customerId);
      const created = this.customerTags.find(
        (customerTag) => customerTag.tag_id === trimmedTagId,
      );
      if (created) return created;
      return await this.partnerCustomerInventoryTagsAPI.getTag(
        customerId,
        trimmedTagId,
      );
    } catch (error) {
      throw error;
    }
  };

  searchProductsBySku = async (sku: string): Promise<ProductListApi[]> => {
    try {
      const trimmed = sku.trim();
      if (!trimmed) return [];

      const numericId = Number(trimmed);
      const isNumericId =
        Number.isInteger(numericId) && numericId > 0 && /^\d+$/.test(trimmed);

      const listParams = {
        page_no: 1,
        limit: 20,
        sort_by: "id" as const,
        sort_type: SortType.DESC,
      };

      const [searchResponse, idResponse] = await Promise.all([
        this.catalogProductsAPI.listProducts({
          ...listParams,
          search: trimmed,
        }),
        isNumericId
          ? this.catalogProductsAPI.listProducts({
              ...listParams,
              limit: 1,
              id: numericId,
            })
          : Promise.resolve({ list: [] as ProductListApi[] }),
      ]);

      const byId = new Map<number, ProductListApi>();
      for (const product of [
        ...(idResponse?.list ?? []),
        ...(searchResponse?.list ?? []),
      ]) {
        if (product.id) byId.set(product.id, product);
      }
      const list = Array.from(byId.values());

      const exactMatches = list.filter((product) => {
        const skuMatch =
          product.sku?.trim().toLowerCase() === trimmed.toLowerCase();
        const idMatch = isNumericId && product.id === numericId;
        return skuMatch || idMatch;
      });
      return exactMatches.length > 0 ? exactMatches : list;
    } catch (error) {
      throw error;
    }
  };

  fetchProductVariants = async (
    productId: number,
  ): Promise<ProductVariantApi[]> => {
    try {
      if (!productId) {
        throw new Error("Invalid product ID");
      }
      const variants = await this.catalogProductsAPI.fetchVariants(productId);
      return variants.filter((variant) => variant.active !== false);
    } catch (error) {
      throw error;
    }
  };

  loadVariants = async (
    inventoryId: number,
    params: InventoryDetailsListParams = {},
  ) => {
    try {
      const page_no = this.normalizePage(params.page_no ?? this.variantsPageNo);
      const limit = this.normalizeLimit(params.limit ?? this.variantsLimit);
      const search =
        "search" in params
          ? params.search?.trim() || undefined
          : this.lastVariantsParams.search;
      const tagId =
        "tagId" in params
          ? params.tagId?.trim() || undefined
          : this.lastVariantsParams.tagId;
      const sortBy =
        "sortBy" in params
          ? params.sortBy?.trim() || undefined
          : this.lastVariantsParams.sortBy;
      const sortType =
        "sortType" in params
          ? params.sortType
          : this.lastVariantsParams.sortType;
      this.lastVariantsParams = { search, tagId, sortBy, sortType };

      const response = await this.inventoryAPI.getVariants({
        inventoryId,
        search,
        tagId,
        page_no,
        limit,
        sortBy,
        sortType,
      });

      runInAction(() => {
        this.inventoryId = inventoryId;
        this.variants = response?.variants ?? [];
        this.variantsPageNo = response?.page_no ?? page_no;
        this.variantsLimit = response?.limit ?? limit;
        this.variantsTotalCount = response?.total_count ?? 0;
        this.variantsTotalPages = response?.total_pages ?? 0;
        this.variantsSortBy = sortBy || "";
        this.variantsSortType = sortType ?? SortType.DESC;
      });
    } catch (error) {
      runInAction(() => {
        this.variants = [];
        this.variantsTotalCount = 0;
        this.variantsTotalPages = 0;
      });
      throw error;
    }
  };

  loadPacks = async (
    inventoryId: number,
    params: InventoryDetailsListParams = {},
  ) => {
    try {
      const page_no = this.normalizePage(params.page_no ?? this.packsPageNo);
      const limit = this.normalizeLimit(params.limit ?? this.packsLimit);
      const search =
        "search" in params
          ? params.search?.trim() || undefined
          : this.lastPacksParams.search;
      const sortBy =
        "sortBy" in params
          ? params.sortBy?.trim() || undefined
          : this.lastPacksParams.sortBy;
      const sortType =
        "sortType" in params
          ? params.sortType
          : this.lastPacksParams.sortType;
      this.lastPacksParams = { search, sortBy, sortType };

      const response = await this.inventoryAPI.listPacks({
        inventoryId,
        search,
        page_no,
        limit,
        sortBy,
        sortType,
      });

      runInAction(() => {
        this.inventoryId = inventoryId;
        this.packs = response?.packs ?? [];
        this.packsPageNo = response?.page_no ?? page_no;
        this.packsLimit = response?.limit ?? limit;
        this.packsTotalCount = response?.total_count ?? 0;
        this.packsTotalPages = response?.total_pages ?? 0;
        this.packsSortBy = sortBy || "";
        this.packsSortType = sortType ?? SortType.DESC;
      });
    } catch (error) {
      runInAction(() => {
        this.packs = [];
        this.packsTotalCount = 0;
        this.packsTotalPages = 0;
      });
      throw error;
    }
  };

  load = async (
    inventoryId: number,
    params: { search?: string; tagId?: string } = {},
  ) => {
    const normalizedSearch = params.search?.trim() || undefined;
    const tagId = params.tagId?.trim() || undefined;
    await Promise.all([
      this.loadVariants(inventoryId, {
        search: normalizedSearch,
        tagId,
        page_no: this.variantsPageNo,
        limit: this.variantsLimit,
      }),
      this.loadPacks(inventoryId, {
        search: normalizedSearch,
        page_no: this.packsPageNo,
        limit: this.packsLimit,
      }),
    ]);
  };

  getPackItems = async (packId: number): Promise<InventoryPackItem[]> => {
    try {
      const pack: InventoryPack = await this.inventoryAPI.getPack(packId);
      return pack?.items ?? [];
    } catch (error) {
      throw error;
    }
  };

  private reloadVariants = async (
    overrides: InventoryDetailsListParams = {},
  ): Promise<void> => {
    if (!this.inventoryId) return;
    await this.loadVariants(this.inventoryId, {
      search: this.lastVariantsParams.search,
      tagId: this.lastVariantsParams.tagId,
      sortBy: this.lastVariantsParams.sortBy,
      sortType: this.lastVariantsParams.sortType,
      page_no: this.variantsPageNo,
      limit: this.variantsLimit,
      ...overrides,
    });
  };

  private reloadPacks = async (
    overrides: InventoryDetailsListParams = {},
  ): Promise<void> => {
    if (!this.inventoryId) return;
    await this.loadPacks(this.inventoryId, {
      search: this.lastPacksParams.search,
      sortBy: this.lastPacksParams.sortBy,
      sortType: this.lastPacksParams.sortType,
      page_no: this.packsPageNo,
      limit: this.packsLimit,
      ...overrides,
    });
  };

  createPack = async (
    payload: Omit<InventoryPackCreateRequest, "inventoryId">,
  ): Promise<void> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Invalid inventory ID");
      }
      await this.inventoryAPI.createPack({
        ...payload,
        inventoryId: this.inventoryId,
      });
      runInAction(() => {
        this.packsPageNo = 1;
      });
      await this.reloadPacks({ page_no: 1 });
    } catch (error) {
      throw error;
    }
  };

  patchPack = async (
    packId: number,
    payload: InventoryPackUpdateRequest,
  ): Promise<void> => {
    try {
      await this.inventoryAPI.patchPack(packId, payload);
      await this.reloadPacks();
    } catch (error) {
      throw error;
    }
  };

  deletePack = async (packId: number): Promise<void> => {
    try {
      await this.patchPack(packId, { active: 0 });
    } catch (error) {
      throw error;
    }
  };

  searchVariants = async (search: string): Promise<InventoryVariant[]> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Invalid inventory ID");
      }
      const response = await this.inventoryAPI.getVariants({
        inventoryId: this.inventoryId,
        search: search.trim() || undefined,
      });
      return response?.variants ?? [];
    } catch (error) {
      throw error;
    }
  };

  replacePackItems = async (
    packId: number,
    items: InventoryPackItemInput[],
  ): Promise<void> => {
    try {
      await this.inventoryAPI.replaceBom(packId, { items });
      await this.reloadPacks();
    } catch (error) {
      throw error;
    }
  };

  getSkuMovements = async (
    clientSku: string,
    limit = 50,
  ): Promise<InventoryMovementRow[]> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Invalid inventory ID");
      }

      const response = await this.inventoryAPI.getMovements({
        inventoryId: this.inventoryId,
        clientSku,
        limit,
        cursor: 0,
      });
      return response?.rows ?? [];
    } catch (error) {
      throw error;
    }
  };

  adjustItem = async (payload: InventoryAdjustRequest): Promise<void> => {
    try {
      await this.inventoryAPI.adjust(payload);
      await this.reloadVariants();
    } catch (error) {
      throw error;
    }
  };

  adjustImportCsv = async (
    inventoryId: number,
    note: string,
    csvFile: File,
  ): Promise<InventoryAdjustImportCsvResult> => {
    try {
      const result = await this.inventoryAPI.adjustImportCsv(
        inventoryId,
        note,
        csvFile,
      );
      const resolvedInventoryId = inventoryId || this.inventoryId;
      if (resolvedInventoryId) {
        runInAction(() => {
          this.inventoryId = resolvedInventoryId;
        });
        await this.reloadVariants();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  inboundImportCsv = async (
    inventoryId: number,
    note: string,
    csvFile: File,
  ): Promise<InventoryInboundImportCsvResult> => {
    try {
      const result = await this.inventoryAPI.inboundImportCsv(
        inventoryId,
        note,
        csvFile,
      );
      const resolvedInventoryId = inventoryId || this.inventoryId;
      if (resolvedInventoryId) {
        runInAction(() => {
          this.inventoryId = resolvedInventoryId;
          this.variantsPageNo = 1;
        });
        await this.reloadVariants({ page_no: 1 });
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  importSkuCsv = async (
    inventoryId: number,
    note: string,
    csvFile: File,
  ): Promise<InventorySkuImportCsvResult> => {
    try {
      const result = await this.inventoryAPI.importSkuCsv(
        inventoryId,
        note,
        csvFile,
      );
      const resolvedInventoryId = inventoryId || this.inventoryId;
      if (resolvedInventoryId) {
        runInAction(() => {
          this.inventoryId = resolvedInventoryId;
        });
        await Promise.all([this.reloadVariants(), this.reloadPacks()]);
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  updateVariantMeta = async (
    variantId: number,
    payload: InventoryVariantUpdateRequest,
  ): Promise<void> => {
    try {
      await this.inventoryAPI.updateVariant(variantId, payload);
      const reloadTasks = [this.reloadVariants()];
      if (payload.clientSku?.trim()) {
        reloadTasks.push(this.reloadPacks());
      }
      await Promise.all(reloadTasks);
    } catch (error) {
      throw error;
    }
  };

  deleteVariantBySku = async (clientSku: string): Promise<void> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Invalid inventory ID");
      }
      const trimmedSku = clientSku.trim();
      if (!trimmedSku) {
        throw new Error("Client SKU is required");
      }
      await this.inventoryAPI.deleteVariantBySku(this.inventoryId, trimmedSku);
      await this.reloadVariants();
    } catch (error) {
      throw error;
    }
  };

  inwardVariants = async (
    inventoryId: number,
    lines: InventoryInwardLineInput[],
    orderId?: number,
  ): Promise<void> => {
    try {
      const resolvedInventoryId = inventoryId || this.inventoryId || 0;
      const resolvedOrderId = orderId && orderId > 0 ? orderId : undefined;

      if (!resolvedInventoryId && !resolvedOrderId) {
        throw new Error("Inbound requires inventory ID or order ID");
      }
      if (!lines.length) {
        throw new Error("Add at least one variant to inward");
      }

      const movementLines: InventoryMovementLine[] = [];
      for (const line of lines) {
        const clientSku = line.clientSku.trim();
        const sku = (line.sku || line.catalogSku || "").trim();
        if (!clientSku || !sku) {
          throw new Error("Each line requires SKU and client SKU");
        }
        if (
          !Number.isFinite(line.qty) ||
          line.qty < 0 ||
          !Number.isInteger(line.qty)
        ) {
          throw new Error("Each line requires a quantity of 0 or more");
        }

        const movementLine: InventoryMovementLine = {
          clientSku,
          sku,
          qty: line.qty,
        };
        if (line.productId) {
          movementLine.productId = line.productId;
        }
        if (line.packSize != null && Number.isFinite(line.packSize)) {
          movementLine.packSize = line.packSize;
        }
        if (line.boxSize?.trim()) {
          movementLine.boxSize = line.boxSize.trim();
        }
        if (line.tagId?.trim()) {
          movementLine.tagId = line.tagId.trim();
        }
        if (line.minThreshold != null && Number.isInteger(line.minThreshold)) {
          movementLine.minThreshold = line.minThreshold;
        }
        movementLines.push(movementLine);
      }

      const payload: InventoryInboundRequest = {
        lines: movementLines,
        note: "Inventory inward",
      };
      if (resolvedOrderId) {
        payload.orderId = resolvedOrderId;
      } else {
        payload.inventoryId = resolvedInventoryId;
      }

      const result = await this.inventoryAPI.inbound(payload);

      runInAction(() => {
        this.inventoryId = result?.inventoryId || resolvedInventoryId || null;
        this.variantsPageNo = 1;
      });
      await this.reloadVariants({ page_no: 1 });
    } catch (error) {
      throw error;
    }
  };
}
